/*
 * 工具调用详情 —— 深色主题代码块
 *
 * 把「入参 / 结果 / 错误」合并到一个深色块里, 用 `// xxx` 注释行分段;
 * 轻量 JSON-ish 语法高亮(注释 / key / 字符串 / 数字 / 布尔/null / 标点);
 * 右上角浮动 copy 按钮, 一键复制合并后的全文到剪贴板.
 */

import React, { useState } from 'react'
import { CheckIcon, CopyIcon } from './icons'
import './CodeDetailBlock.scss'

export interface CodeSection {
  label: string
  body: string
}

// 一行内的 token: 字符串(可能是 key)/ 数字 / true|false|null
const TOKEN_RE =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\b\d+(?:\.\d+)?\b)|\b(true|false|null)\b/g

function highlightLine(line: string, lineKey: string): React.ReactNode {
  // `// xxx` 注释行整行走注释色
  if (line.trimStart().startsWith('//')) {
    return <span className="aiClerk-code__comment">{line}</span>
  }
  const nodes: React.ReactNode[] = []
  let last = 0
  let i = 0
  let m: RegExpExecArray | null
  TOKEN_RE.lastIndex = 0
  while ((m = TOKEN_RE.exec(line))) {
    if (m.index > last) nodes.push(line.slice(last, m.index))
    if (m[1] != null) {
      // 字符串后面紧跟冒号 → 当作 key
      if (m[2]) {
        nodes.push(
          <span key={`${lineKey}-${i++}`} className="aiClerk-code__key">
            {m[1]}
          </span>
        )
        nodes.push(
          <span key={`${lineKey}-${i++}`} className="aiClerk-code__punc">
            {m[2]}
          </span>
        )
      } else {
        nodes.push(
          <span key={`${lineKey}-${i++}`} className="aiClerk-code__string">
            {m[1]}
          </span>
        )
      }
    } else if (m[3] != null) {
      nodes.push(
        <span key={`${lineKey}-${i++}`} className="aiClerk-code__number">
          {m[3]}
        </span>
      )
    } else if (m[4] != null) {
      nodes.push(
        <span key={`${lineKey}-${i++}`} className="aiClerk-code__bool">
          {m[4]}
        </span>
      )
    }
    last = TOKEN_RE.lastIndex
  }
  if (last < line.length) nodes.push(line.slice(last))
  return nodes
}

function highlight(text: string): React.ReactNode {
  return text.split('\n').map((line, idx) => (
    <React.Fragment key={idx}>
      {idx > 0 ? '\n' : null}
      {highlightLine(line, String(idx))}
    </React.Fragment>
  ))
}

/** clipboard API 不可用时的兜底(非安全上下文 / 老内核) */
function fallbackCopy(text: string, done: () => void): void {
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.top = '-1000px'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    done()
  } catch {
    /* ignore */
  }
}

const CodeDetailBlock: React.FC<{ sections: CodeSection[] }> = ({
  sections
}) => {
  const [copied, setCopied] = useState(false)
  if (sections.length === 0) return null

  // 合并文本: 渲染与复制同一份, 段间 `// label` 注释分隔
  const combined = sections.map((s) => `// ${s.label}\n${s.body}`).join('\n')

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    const done = () => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(combined)
          .then(done)
          .catch(() => fallbackCopy(combined, done))
      } else {
        fallbackCopy(combined, done)
      }
    } catch {
      fallbackCopy(combined, done)
    }
  }

  return (
    <div className="aiClerk-code">
      <button
        type="button"
        className={'aiClerk-code__copy' + (copied ? ' is-copied' : '')}
        onClick={handleCopy}
        aria-label={copied ? '已复制' : '复制入参和结果'}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
      <pre className="aiClerk-code__body">{highlight(combined)}</pre>
    </div>
  )
}

export default CodeDetailBlock
